import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ComponentStore } from '@ngrx/component-store';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, concatMap, map, takeUntil } from 'rxjs/operators'
import { FileUploadService } from 'src/app/core/file-upload';
import { LayoutUtilsService, TypesUtilsService } from 'src/app/core/_base/crud';
import { environment } from 'src/environments/environment';
import { ImageViewerNgbModalComponent } from '../../target-property/valuation-process-edit/_sub/image-viewer-ngb-modal/image-viewer-ngb-modal.component';
import { ImageViewerDialogComponent } from '../image_viewer/image-viewer.dialog.component';

export interface UploadFileState {
  status: UploadFileStatus;
  uploadedFiles: any[];
  error: string | null;
  progres: number | null;
}

export enum UploadFileStatus {
  Ready = 'Ready',
  Failed = 'Failed',
  Requested = 'Requested',
  Started = 'Started',
  Completed = 'Completed'
}

@Component({
  selector: 'kt-condition-rating-documents',
  templateUrl: './condition-rating-documents.component.html',
  styleUrls: ['./condition-rating-documents.component.scss'],
  providers: [ComponentStore]
})
export class ConditionRatingDocumentsComponent implements OnInit {
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  @Input() parent: 'valuation' | 'inspection' | 'comparable-info' = 'inspection';
  @Input() files: any[];
  @Input() readonly: boolean = false;
  @Input() useMatDialog: boolean = true;

  uploadedFiles: any[] = [];
  basePath = environment.baseApiUrl;

  displayedColumns = ['type', 'title', 'descr', 'actions'];
  dataSource = new MatTableDataSource();
  docs = ['xlsx', 'xls', 'doc', 'docx'];

  error$: Observable<string>;
  completed$: Observable<boolean>;
  progress$: Observable<number>;
  isInProgress$: Observable<boolean>;
  isReady$: Observable<boolean>;
  hasFailed$: Observable<boolean>;
  uploadedFiles$: Observable<any[]>;
  cancelEvent$: Subject<void> = new Subject();

  errorFields = [];
  hasErrors: boolean = false;
  showTitleRequiredError$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private readonly componentStore: ComponentStore<UploadFileState>,
    private fileUploadService: FileUploadService,
    public typesUtilsService: TypesUtilsService,
    private layoutUtilsService: LayoutUtilsService,
    private dialog: MatDialog,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.componentStore.setState({
      uploadedFiles: this.files,
      error: null,
      progres: null,
      status: UploadFileStatus.Ready
    })
    this.completed$ = this.componentStore.select(state => state.status === UploadFileStatus.Completed);
    this.error$ = this.componentStore.select(state => state.error);
    this.progress$ = this.componentStore.select(state => state.progres);
    this.isInProgress$ = this.componentStore.select(state => state.status === UploadFileStatus.Started && state.progres >= 0);
    this.isReady$ = this.componentStore.select(state => state.status === UploadFileStatus.Ready);
    this.hasFailed$ = this.componentStore.select(state => state.status === UploadFileStatus.Failed);
    this.uploadedFiles$ = this.componentStore.select(state => state.uploadedFiles);
    this.uploadedFiles$.subscribe(files => {
      if (files) {
        this.uploadedFiles = files;
        this.dataSource.data = files;
      }
    })
  }

  checkValidation(isComplete: boolean): boolean {
    this.errorFields = [];
    let errorFields = new Set<string>();
    let isValid = true;
    this.hasErrors = false;
    this.showTitleRequiredError$.next(false);
    let hasTitleError = false;

    if (isComplete && this.uploadedFiles.length < 1) {
      this.hasErrors = true;
      isValid = false;
      errorFields.add('no_condition_document');
    } else if (isComplete) {
      this.uploadedFiles.forEach(file => {
        if (file.title == null || file.title == '') {
          this.hasErrors = true;
          hasTitleError = true;
          isValid = false;
          errorFields.add('no_condition_document_title');
        }
      })
    }
    this.showTitleRequiredError$.next(hasTitleError);

    for (let v of errorFields.values()) {
      this.errorFields.push(v);
    }

    return isValid;
  }

  getDocuments(): any[] {
    return this.uploadedFiles;
  }

  onAddClick() {
    this.fileInput.nativeElement.click();
  }

  cancelUpload() {
    this.cancelEvent$.next();
    this.uploadCancel();
  }

  uploadFiles(event) {
    const files: FileList = event.target.files;
    const firstFile = files.item(0);

    const formData = new FormData();
    Array.from(files).forEach((file, i) => {
      formData.append('files[' + i + ']', file, file.name);
    });

    this.uploadFile(formData);

    event.srcElement.value = null;
  }

  editFileDescription(file) {
    const _title = 'Adding Description';
    const _description = '';
    const _waitDesciption = '';
    const _fileTitle = file.title ? file.title : '';
    const _fileDescription = file.descr ? file.descr : '';

    const dialogRef = this.layoutUtilsService.saveElementWithInfo(_title,
      _description,
      _waitDesciption,
      _fileTitle,
      true,
      _fileDescription);
    dialogRef.afterClosed().subscribe(res => {
      if (!res) {
        return;
      }
      const index = this.uploadedFiles.indexOf(file);
      const editedObj = Object.assign({}, file);
      editedObj.title = res.title;
      editedObj.descr = res.descr;
      editedObj._isEditMode = true;
      this.uploadedFiles = Object.assign([], this.uploadedFiles, { [index]: editedObj });
      this.componentStore.setState((state) => {
        return {
          ...state,
          uploadedFiles: this.uploadedFiles
        }
      })
    });
  }

  onInputFocusOut(event: FocusEvent, file) {
    const target = event.target as HTMLInputElement;
    const val = target.value.trim();
    const index = this.uploadedFiles.indexOf(file);
    const editedObj = Object.assign({}, file);
    editedObj.title = val;
    this.uploadedFiles = Object.assign([], this.uploadedFiles, { [index]: editedObj });
    this.componentStore.setState((state) => {
      return {
        ...state,
        uploadedFiles: this.uploadedFiles
      }
    })
  }

  deleteUploadedFile(file) {
    this.componentStore.setState((state) => {
      let currentUploadedFiles = Object.assign([], state.uploadedFiles);
      let indx = -1;
      if (file.id) {
        indx = currentUploadedFiles.findIndex(f => f.id == file.id);
      } else {
        indx = currentUploadedFiles.findIndex(f => f.name == file.name);
      }
      if (indx >= 0) {
        currentUploadedFiles.splice(indx, 1);
      }
      return {
        ...state,
        uploadedFiles: currentUploadedFiles
      }
    })
  }

  previewUploadedFile(file) {
    if (this.docs.indexOf(this.typesUtilsService.getFileType(file.name)) > -1) {
      window.open(this.basePath + file.path + '/' + file.name);
    } else {
      if (this.useMatDialog) {
        this.dialog.open(ImageViewerDialogComponent, {
          data: {
            picture: file.path + '/' + file.name,
            type: this.typesUtilsService.getFileType(file.name)
          }
        })
      } else {
        const modalRef = this.modalService.open(ImageViewerNgbModalComponent, {
          windowClass: 'image_viewer_ngb_modal',
          backdrop: 'static',
          scrollable: true,
        })
        modalRef.componentInstance.title = file.title;
        modalRef.componentInstance.picture = file.path + '/' + file.name,
        modalRef.componentInstance.type = this.typesUtilsService.getFileType(file.name);
      }
    }
  }

  private uploadFile = this.componentStore.effect((formData$: Observable<FormData>) => {
    return formData$.pipe(
      concatMap(formdata =>
        this.fileUploadService.uploadFile(formdata).pipe(
          takeUntil(
            this.cancelEvent$
          ),
          map(event => this.handleHttpEvent(event)),
          catchError(error => {
            this.uploadFailure(`Too large file. Max file upload is: 16mb.`);
            return EMPTY
          })
        ))
    )
  });

  private handleHttpEvent(event: HttpEvent<any>) {
    switch (event.type) {
      case HttpEventType.Sent: {
        this.uploadStarted();
        return;
      }
      case HttpEventType.UploadProgress: {
        this.uploadProgress(Math.round((100 * event.loaded) / event.total))
        return;
      }
      case HttpEventType.ResponseHeader:
      case HttpEventType.Response: {
        if (event.status === 200) {
          let response_body;
          if (event.type == HttpEventType.Response) {
            response_body = event.body;
          }
          this.uploadCompleted(response_body);
          return;
        }
        if (event.status == 400) {
          this.uploadFailure('Bad Request, Please check your form data.')
          return;
        }
        if (event.status === 403) {
          this.uploadFailure('The Server is refusing action, Please check your form data.')
          return;
        }
        if (event.status === 404) {
          this.uploadFailure('Not found, API have not developed yet.')
          return;
        }
        if (event.status === 500) {
          this.uploadFailure('Internal Server Error, Please inform us by email.')
          return;
        }
        if (event.status >= 500) {
          this.uploadFailure(`Server error ${event.status}, Please inform us by email`)
          return;
        }
        if (event.status >= 400) {
          this.uploadFailure(`Client error ${event.status}, Please check your inserted data`)
          return;
        }
        this.uploadFailure(`Request error ${event.status}, Please check action`)
        return;
      }
      default: {
        this.uploadFailure(`Unknown event: ${JSON.stringify(event)}`)
        return;
      }
    }
  }
  j
  private uploadStarted() {
    this.componentStore.setState((state) => {
      return {
        ...state,
        status: UploadFileStatus.Started,
        progres: 0
      }
    })
  }
  private uploadProgress(progress: number) {
    this.componentStore.setState((state) => {
      return {
        ...state,
        progres: progress
      }
    })
  }
  private uploadCompleted(res: any) {
    this.componentStore.setState((state) => {
      const uploadedFiles: any[] = [];
      state.uploadedFiles.forEach(f => uploadedFiles.push(f));
      if (res) {
        res.uploaded_files.forEach(f => uploadedFiles.push(f));
      }
      return {
        ...state,
        status: UploadFileStatus.Completed,
        progres: 100,
        error: null,
        uploadedFiles
      }
    })
  }
  private uploadFailure(error: string) {
    this.componentStore.setState((state) => {
      return {
        ...state,
        status: UploadFileStatus.Failed,
        error,
        progres: null,
      }
    })
  }
  private uploadCancel() {
    this.componentStore.setState((state) => {
      return {
        ...state,
        status: UploadFileStatus.Ready,
        progres: null,
        error: null
      }
    })
  }

  getType(url): string {
    const type = this.typesUtilsService.getFileType(url);
    if (type === 'docx') {
      return 'Word'
    } else {
      return type.toUpperCase();
    }
  }
}
